import { createEntityAdapter, createSlice, PayloadAction, EntityId } from "@reduxjs/toolkit";

import { RootState } from "../Store";
import { resetState } from "../actions/reset-actions";
import { patientStatusSliceProps } from "../../models/patient-status.model";
import { PatientStatusThunkApis } from "../actions/patient-status-actions";
import { patientStatusProps } from "../../models/patient.model";

const patientStatusAdapter = createEntityAdapter<patientStatusProps>({
    selectId: (status) => {
        // Ensure PatientStatusId is not null
        if (status.PatientStatusId === null) {
            throw new Error('PatientStatusId cannot be null');
        }
        return status.PatientStatusId as EntityId;
    },
    sortComparer: (a, b) => {
        if (a.PatientStatusId === null || b.PatientStatusId === null) {
            return 0;
        }
        return b.PatientStatusId! - a.PatientStatusId!;
    },
});

const initialState = patientStatusAdapter.getInitialState<patientStatusSliceProps>({
    pageSize: 10,
    currentPage: 0,
    listLoading: false,
    totalPatientStatus: 0,
    searchTerm: null,
    patientStatusDetails: null,
    openPatientStatusDialog: false,
    filteredPatientStatus: [],
    forceEditMsg: null
});

const PatientStatusSlice = createSlice({
    name: 'patientStatus',
    initialState,
    reducers: {
        patchState(state, action: PayloadAction<Partial<patientStatusSliceProps>>) {
            return { ...state, ...action.payload };
        },
        setInitialState(state) {
            return {
                ...state,
                pageSize: 10,
                currentPage: 0,
                listLoading: false,
                totalPatientStatus: 0,
                searchTerm: null,
                patientStatusDetails: null,
                openPatientStatusDialog: false,
                filteredPatientStatus: [],
                forceEditMsg: null
            }
        },
        removePatientStatus: (state, action: PayloadAction<{ id: number }>) => {
            patientStatusAdapter.removeOne(state, action.payload.id);
        },
        updatePatientStatus: (state, action: PayloadAction<{ id: number, name: string }>) => {
            const { id, name } = action.payload;

            patientStatusAdapter.updateOne(state, {
                id,
                changes: { PatientStatusName: name }
            });
        },
    },
    extraReducers: (builder) => {
        // To reset all the state variables
        builder.addCase(resetState, () => initialState);
        // getPatientStatus
        builder.addCase(PatientStatusThunkApis.getPatientStatus.pending, (state) => {
            state.listLoading = true;
        });
        builder.addCase(PatientStatusThunkApis.getPatientStatus.fulfilled, (state, action) => {
            state.listLoading = false;
            const patientStatus = action.payload.data ?? [];
            state.totalPatientStatus = patientStatus.length;
            patientStatusAdapter.setAll(state, patientStatus);
        });
        builder.addCase(PatientStatusThunkApis.getPatientStatus.rejected, (state) => {
            state.listLoading = false;
        });
    },
});

export const patientStatusSelectors = patientStatusAdapter.getSelectors<RootState>((state) => state.patientStatus);

export const PatientStatusSliceActions = PatientStatusSlice.actions;
export default PatientStatusSlice.reducer;